/** @jsxImportSource @emotion/react */
import {
  Button,
  ButtonProps,
  Card,
  Checkbox,
  Icon,
  Logo,
  Typography,
  UtilityButton,
  createStyles,
  useFormatCurrency,
  useTheme,
} from '@quickbit/qb-design-system';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import { Markdown, MerchantLogoSection } from 'components';
import { useMixpanelTracking, useWindow } from 'hooks';
import { useSetOnrampData, useOnrampData } from 'onramp-pages/context';
import { environment } from 'utils/environmentVariables';
import { SkrillLogo } from './SkrillLogo';

const skrillBrandColor = '#862065';

export interface Props {
  children?: ReactNode;
  topChildren?: ReactNode;
  buttonProps?: ButtonProps;
}

export const OnrampLayout = ({ children, topChildren, buttonProps }: Props) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const formatCurrency = useFormatCurrency();
  const { spacing, breakpoints, palette } = useTheme();
  const {
    merchantName,
    logoUrl,
    logoEnabled,
    fiatAmount,
    cryptoAmount,
    address,
    email,
    acceptedTerms,
    paymentMethod,
    receiveOrderPreviewId,
  } = useOnrampData();
  const setOnrampData = useSetOnrampData();

  const [showDetails, setShowDetails] = useState(false);
  const { openWindow } = useWindow();
  const paymentMethodSkrill = paymentMethod === 'skrill';
  const trackEvent = useMixpanelTracking();

  const handleEditAddress = () => {
    trackEvent(
      receiveOrderPreviewId,
      'TopUp - Edit Address - Redirected to webapp'
    );
    openWindow(`${environment.REACT_APP_HOME_URL}/BTC/send`);
  };

  return (
    <Card
      data-testid="layout-wrapper"
      css={[styles.wrapper, paymentMethodSkrill && styles.paymentMethodSkrill]}
    >
      <main css={[styles.container]}>
        <>
          <div css={styles.payMerchantContainer}>
            {paymentMethodSkrill ? (
              <div css={{ display: 'grid', gap: '8px' }}>
                <Typography variant="bodySmall">Payment Method</Typography>
                <SkrillLogo />
              </div>
            ) : logoEnabled && logoUrl ? (
              <MerchantLogoSection
                merchantName={merchantName}
                logoUrl={logoUrl}
              />
            ) : (
              <Typography variant="heading1">
                {formatMessage(
                  {
                    id: 'page.onramp.login.payToMerchant',
                  },
                  { merchantName }
                )}
              </Typography>
            )}
          </div>
        </>
        {topChildren}
      </main>

      {(children || buttonProps) && (
        <Card css={styles.footerCard}>
          <div css={{ marginBottom: spacing.xxxs }}>
            <Logo width={spacing.xl} />
          </div>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              borderBottom: `1px solid ${palette.grey.x200}`,
            }}
            onClick={() => setShowDetails(false)}
          >
            <Typography variant="bodySmall" color={palette.grey.default}>
              Order details
            </Typography>
            {!showDetails && (
              <UtilityButton
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetails(true);
                }}
                text={formatMessage({ id: 'page.onramp.button.edit' })}
              />
            )}
            {showDetails && (
              <div
                css={{
                  svg: { fill: 'none', stroke: palette.grey.default },
                  transform: 'rotate(-90deg)',
                  cursor: 'pointer',
                }}
              >
                <Icon name="chevron" size={spacing.xxs} />
              </div>
            )}
          </div>
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              alignItems: 'baseline',
              width: '100%',
              marginTop: spacing.xxs,
              gap: spacing.xxxs,
              'p:nth-of-type(even)': {
                justifySelf: 'end',
              },
            }}
          >
            {showDetails && (
              <>
                {email && (
                  <>
                    <Typography
                      variant="bodySmallestMedium"
                      color={palette.grey.default}
                    >
                      User email:
                    </Typography>
                    <Typography
                      variant="bodySmallest"
                      color={palette.grey.default}
                    >
                      {email}
                    </Typography>
                  </>
                )}
                <Typography
                  variant="bodySmallestMedium"
                  color={palette.grey.default}
                >
                  {formatMessage({ id: 'page.onramp.send.bitcoin' })}:
                </Typography>
                <Typography variant="bodySmallest" color={palette.grey.default}>
                  {formatCurrency(cryptoAmount)}
                </Typography>
                <Typography
                  variant="bodySmallestMedium"
                  color={palette.grey.default}
                >
                  Address:
                </Typography>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: spacing.xxxs,
                  }}
                >
                  <UtilityButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAddress();
                    }}
                    variant="text"
                    text={formatMessage({ id: 'page.onramp.button.edit' })}
                  />
                  <Typography
                    variant="bodySmallest"
                    color={palette.grey.default}
                  >
                    {address}
                  </Typography>
                </div>
              </>
            )}
          </div>
          <div
            css={{
              marginTop: spacing.xxs,
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant="bodyLarge">
              {formatMessage({ id: 'page.onramp.buy.total' })}
            </Typography>
            <Typography variant="bodyLarge">
              {formatCurrency(fiatAmount)}
            </Typography>
          </div>
          <div
            css={[
              {
                display: 'flex',
                gap: spacing.xxs,
                marginTop: spacing.xxs,
                alignItems: 'center',
              },
              paymentMethodSkrill && {
                a: { color: `${skrillBrandColor} !important` },
              },
            ]}
          >
            <Checkbox
              checked={!!acceptedTerms}
              onChange={(c) => setOnrampData({ acceptedTerms: c })}
            />
            <Typography variant="bodySmallest" color={palette.grey.default}>
              <Markdown
                markdown={formatMessage({
                  id: 'page.onramp.termsAndConditions',
                })}
              />
            </Typography>
          </div>
          {children}
          {buttonProps && (
            <Button
              {...{
                ...buttonProps,
                disabled: buttonProps.disabled || !acceptedTerms,
              }}
              css={{ marginTop: spacing.xxxs }}
            />
          )}
        </Card>
      )}
      {!(children || buttonProps) && (
        <div
          css={{
            [breakpoints.laptopSmall]: {
              marginTop: spacing.m,
            },
          }}
        />
      )}
    </Card>
  );
};

const useStyles = createStyles(
  ({ spacing, breakpoints, palette, shape, sizing }) => ({
    wrapper: {
      height: '100%',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      border: 'none',
      [breakpoints.laptopSmall]: {
        maxWidth: '100%',
        display: 'block',
        height: 'unset',
        borderRadius: `calc(${shape.borderRadius} * 2)`,
        border: `1px solid ${palette.grey.x100}`,
      },
    },
    paymentMethodSkrill: {
      button: {
        '&.MuiButton-contained': { background: skrillBrandColor },
        '&.MuiButton-textPrimary': {
          color: skrillBrandColor,
          '&:focus': {
            outline: `1px solid ${skrillBrandColor}`,
          },
        },
      },
      '.Mui-checked': {
        color: `${skrillBrandColor} !important`,
      },
    },
    cancelButton: {
      position: 'absolute',
      right: spacing.xxxs,
      top: spacing.xxxs,
      cursor: 'pointer',
      [breakpoints.laptopSmall]: {
        right: 0,
        top: spacing.xxs,
      },
    },
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.xxs,
      margin: `0 ${spacing.xxs}`,
      minHeight: `calc(${sizing.small} - ${spacing.m})`,
      textAlign: 'center',
      [breakpoints.laptopSmall]: {
        paddingTop: spacing.xs,
        margin: 0,
      },
    },
    marginWrapper: {
      margin: `0 ${spacing.xxs}`,
      [breakpoints.laptopSmall]: {
        margin: 0,
      },
    },
    footerCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      padding: spacing.s,
      paddingBottom: spacing.m,
      [breakpoints.laptopSmall]: {
        marginTop: spacing.m,
        borderRadius: `calc(${shape.borderRadius} * 2)`,
      },
    },
    progressBar: {
      background: palette.primary.default,
      height: spacing.xxxs,
      borderRadius: `0 ${shape.borderRadius} ${shape.borderRadius} 0`,
    },
    progressContainer: {
      background: palette.primary.x50,
      height: spacing.xxxs,
      width: '100%',
    },
    progressWidth: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    },
    payMerchantContainer: {
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center',
      gap: spacing.xxxs,
    },
  })
);
